import { Tooltip, TooltipTrigger } from "@artis/ui";
import { TruncatedTooltip } from "../../../components/TruncatedTooltip";
import cn from "mcn";
import { useRef, useState, useLayoutEffect } from "react";

import { splitCellRef } from "../../../../data/formatting/cell-ref";
import { useToggleCellSelection } from "../../useToggleCellSelection";
import { useAllPageProducts, useProductById } from "../../../../data";
import { Button, IconButton } from "../../../ui";
import { PointerIcon, CloseIcon } from "../../../icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { Dropdown } from "../../../ui/dropdown";
import { useUserId } from "../../../../context/auth/hooks";

export { TruncatedTooltip };

// Tracks an element's rendered width so descendants can be given an explicit pixel width.
// Needed because `TooltipTrigger` wraps its children in an `inline-flex` box, which shrinks to
// content size - a percentage width (e.g. `w-full`) on a truncated span inside it can't resolve
// against that box and falls back to the untruncated content size, causing overflow.
const useMeasuredWidth = <T extends HTMLElement>(fallback: number) => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
};

const hasColonRegex = /:/;
export const InputOrReference = ({
  pageId,
  value,
  onChange,
  width = 150,
  disabled = false,
  /** When set, wraps the whole control in one tooltip and suppresses nested ones. */
  tooltip,
}: {
  pageId: string;
  value: string;
  onChange: (value: string) => void;
  width?: number;
  disabled?: boolean;
  tooltip?: string;
}) => {
  const [selecting, selectCell] = useToggleCellSelection();
  const [iconRef, iconWidth] = useMeasuredWidth<HTMLDivElement>(27);
  // Measured (not hand-computed) so this stays correct regardless of the icon button's actual
  // rendered size - passed down as an explicit pixel width, see `useMeasuredWidth` above.
  const contentWidth = Math.max(0, width - iconWidth - 8);

  const selectLimitButton = (
    <IconButton
      disabled={selecting.active || disabled}
      onClick={() => {
        if (disabled) return;
        selectCell().then((x) => {
          if (!x) return;
          onChange(x);
        });
      }}
    >
      <PointerIcon className="w-[18px] h-[18px]" />
    </IconButton>
  );

  const content = (
    <div data-testid="input-or-reference" className="flex gap-2 items-center shrink-0" style={{ width: `${width}px` }}>
      {hasColonRegex.test(value) ? (
        <SelectionRef
          pageId={pageId}
          value={value}
          onRemove={() => !disabled && onChange("")}
          disabled={disabled}
          width={contentWidth}
          suppressTooltip={!!tooltip}
        />
      ) : (
        <input
          type="text"
          value={value}
          inputMode="numeric"
          placeholder=""
          disabled={disabled}
          className="px-2 border border-outline rounded-lg text-sm m-0! bg-surface-1 box-border transition-colors hover:bg-surface-2 hover:border-outline-variant focus:bg-surface-2 focus:outline-[1.5px] focus:outline-offset-[-1.5px] focus:outline-[#1D3DD5] dark:focus:outline-[#778CD9] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface-1 disabled:hover:border-outline"
          style={{ height: "27px", width: `${contentWidth}px` }}
          onChange={(e) => {
            if (disabled) return;
            const newValue = e.target.value;
            // Allow empty, numbers, decimal point, and negative sign
            if (newValue === "" || /^-?\d*\.?\d*$/.test(newValue)) {
              onChange(newValue);
            }
          }}
        />
      )}
      <div ref={iconRef} className="shrink-0">
        {/* Parent `tooltip` already covers this control — don't nest another one. */}
        {tooltip ? (
          selectLimitButton
        ) : (
          <TooltipTrigger>
            {selectLimitButton}
            <Tooltip>{disabled ? "Alert is expired" : "Select limit value"}</Tooltip>
          </TooltipTrigger>
        )}
      </div>
    </div>
  );

  if (!tooltip) return content;

  return (
    <TooltipTrigger>
      {content}
      <Tooltip>{tooltip}</Tooltip>
    </TooltipTrigger>
  );
};

const SelectionRef = ({
  value,
  onRemove,
  width = 150,
  disabled = false,
  suppressTooltip = false,
}: {
  pageId: string;
  value: string;
  onRemove: () => void;
  width?: number;
  disabled?: boolean;
  /** Parent already shows a tooltip covering this control. */
  suppressTooltip?: boolean;
}) => {
  const parsed = splitCellRef(value);
  const colId = parsed?.columnId ?? "";
  const productId = parsed?.productId ?? "";
  const rowId = parsed?.period ?? "";
  const pageProducts = useAllPageProducts(useUserId());
  const product = useProductById(
    pageProducts.data?.find((p) => p.grid_id === colId)?.product_id ?? productId ?? undefined,
  );
  const month = rowId; // Already absolute period
  const [closeIconRef, closeIconWidth] = useMeasuredWidth<HTMLDivElement>(20);
  // Explicit pixel width (measured, not a hand-computed constant) rather than `w-full`: this span
  // can end up nested inside `TooltipTrigger`'s `inline-flex` wrapper once it's truncated, and a
  // percentage width can't resolve there - see `useMeasuredWidth` above.
  const textWidth = Math.max(0, width - 8 - 2 - 4 - (disabled ? 0 : closeIconWidth));
  const label = (
    <span data-truncate-check className="block truncate cursor-default text-sm" style={{ width: `${textWidth}px` }}>
      {product.data?.name} [{month}]
    </span>
  );
  return (
    <div
      className="border rounded-md px-1 flex items-center gap-1 bg-surface-1 box-border"
      style={{
        height: "27px",
        width: `${width}px`,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
    >
      {suppressTooltip ? (
        label
      ) : (
        <TruncatedTooltip title={`${product.data?.name} [${month}]`}>{label}</TruncatedTooltip>
      )}
      {!disabled && (
        <div ref={closeIconRef} className="shrink-0">
          <CloseIcon onClick={onRemove} className="!w-[20px] !h-[20px] !p-0" />
        </div>
      )}
    </div>
  );
};

export const AddRowButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="p-3">
      <TooltipTrigger>
        <div>
          <Button
            variant="text"
            color="primary"
            size="sm"
            icon={faPlus}
            onClick={onClick}
            className="font-medium whitespace-nowrap"
          >
            Add New Rule
          </Button>
        </div>
        <Tooltip>Add a rule with higher priority</Tooltip>
      </TooltipTrigger>
    </div>
  );
};

export const ManageTable = (props: React.ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn("grid w-full", props.className)}
      style={{
        marginTop: "var(--spacing-scale-small-400, 12px)",
        gap: "0 var(--spacing-scale-small-300, 9px)",
        ...props.style,
      }}
    />
  );
};

const ManageTableHeader = (props: React.ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        "col-span-full grid grid-cols-subgrid text-foreground-muted font-medium bg-card border-b border-outline px-4 rounded-t-md sticky top-0 z-20",
        props.className,
      )}
      style={{
        paddingTop: "var(--spacing-scale-small-200, 6px)",
        paddingBottom: "var(--spacing-scale-small-200, 6px)",
        ...props.style,
      }}
    />
  );
};
ManageTable.Header = ManageTableHeader;

const ManageTableRow = (props: React.ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        "col-span-full grid grid-cols-subgrid items-center text-foreground text-sm px-4 h-min bg-card-muted border-b",
        props.className,
      )}
      style={{
        paddingTop: "var(--spacing-scale-small-200, 6px)",
        paddingBottom: "var(--spacing-scale-small-200, 6px)",
        ...props.style,
      }}
    />
  );
};
ManageTable.Row = ManageTableRow;

export const RangeSelect = ({
  value,
  onValueChange,
}: {
  value: "page" | "range";
  onValueChange: (value: "page" | "range") => void;
}) => (
  <div
    className="mx-4"
    style={{ marginTop: "var(--spacing-scale-small-400, 12px)", marginBottom: "var(--spacing-scale-small-400, 12px)" }}
  >
    <Dropdown
      value={value}
      onChange={(val) => onValueChange(val as "page" | "range")}
      options={[
        { value: "page", label: "Page" },
        { value: "range", label: "Range" },
      ]}
      className="w-min h-[27px]"
    />
  </div>
);
